import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BaseMetricLogCard } from "@/core/components/tracking";
import { getCravingDates, logCraving, ApiResult } from "@/lib/api";
import { useProductContext } from "@/contexts/ProductContext";

interface CravingLogCardProps {
  onComplete?: () => void;
  onDataUpdate?: () => void;
  isLoading?: boolean;
}

export function CravingLogCard({ onComplete, onDataUpdate, isLoading }: CravingLogCardProps) {
  const { t } = useTranslation();
  const { currentProduct } = useProductContext();

  return (
    <BaseMetricLogCard
      // Display
      icon={AlertCircle}
      gradientClasses="from-emerald-500 to-teal-600"
      borderHoverClasses="hover:border-emerald-500/40"

      // Input type
      inputType="slider"
      sliderConfig={{
        min: 1,
        max: 5,
        step: 1,
        defaultValue: 3,
        showScale: false,
      }}

      // Validation
      minValue={1}
      maxValue={5}

      // Pre-translated strings
      cardTitle={t('mindcotine:actions.logCraving', 'Log Craving')}
      cardDescription={t('mindcotine:actions.cravingHelp', 'Track your cravings')}
      dialogTitle={t('mindcotine:actions.logCraving', 'Log Craving')}
      dialogDescription={t('mindcotine:actions.cravingHelp', 'Track your cravings')}
      inputLabel={t('mindcotine:actions.intensityFor', 'Intensity for')}
      inputPlaceholder="1-5"
      notesPlaceholder={t('mindcotine:actions.notesPlaceholder', 'Add any notes...')}
      loggedTitle={t('mindcotine:actions.cravingLogged', 'Craving Logged')}
      loggedDescription={t('mindcotine:actions.cravingLoggedDescription', 'Your craving has been recorded.')}
      errorInvalidInput={t('common:errors.invalidInput', 'Invalid Input')}
      errorInvalidValue={t('common:errors.invalidNumber', 'Please enter a valid number.')}

      // Analytics
      analyticsEvent="Craving Logged"
      analyticsDataMapper={(value, date) => ({
        intensity: value,
        date,
      })}

      // Popup triggers
      popupTriggers={[
        { trigger: "craving_logged" },
        { 
          trigger: "high_intensity_craving", 
          condition: (value) => value >= 4,
          contextMapper: (value) => ({ intensity: value })
        }
      ]}

      // Custom data source functions (cravings table, not progress_logs)
      customFetchDates={async (userId: string): Promise<ApiResult<string[]>> => {
        return getCravingDates(userId);
      }}
      customFetchValue={async () => null}
      customUpdateFunction={async (userId: string, date: Date, value: number, notes?: string, productId?: string): Promise<ApiResult<any>> => {
        const selectedDateTime = new Date(date);
        selectedDateTime.setHours(new Date().getHours());
        selectedDateTime.setMinutes(new Date().getMinutes());
        selectedDateTime.setSeconds(new Date().getSeconds());

        return logCraving(userId, {
          intensity: value,
          trigger: t('mindcotine:actions.manualLogTrigger', 'Manual log'),
          coping_strategy: notes || undefined,
          created_at: selectedDateTime.toISOString(),
        }, productId);
      }}

      // Callbacks
      onComplete={onComplete}
      onDataUpdate={onDataUpdate}
      isLoading={isLoading}

      // Product context
      productId={currentProduct}
    />
  );
}
